import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';
import ImageScroller from '../../components/imagescroller';
import Stl3dSection from './stl3dSection';
import VariantPills, { toggleVariant } from './variantPills';
import GuestBuyBox from './guestBuyBox';
import MiniGraph from '../../components/lineGraph/mini';
import FileList from '../../components/fileViewer/list';
import UrlList from '../../components/urlViewer/list';
import GradientRight from '../../components/gui/gradientRight';
import HelpText from '../../components/gui/helpText';
import StarRating from '../../components/gui/starRating';
import Input from '../../components/form/input';

import Comments from '../../components/comments';
import Likes from '../../components/likes';
import ClipboardCopy from '../../components/gui/clipboardCopy';
import { shareUrlFor } from '../share/shareTargets';

import localUtil from '../../lib/util';
import { decorateExternalLinks } from '../../lib/externalLinks';

const MARKDOWN_OPTIONS = {
	pedantic: false,
	gfm: true,
	breaks: true,
	sanitize: false,
	smartLists: true,
	smartypants: true,
	xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

@withText(props => ({
    installationListHelpText: <Text id='product.installation-list-help'>List of all the places where this product is currently located.</Text>,
    productUsageListHelpText: <Text id='product.usage-list-help'>All logged usage of this product. This can be service of a car, boat trips, shooting practice, etc.</Text>,
    productLoanListHelpText: <Text id='product.loan-list-help'>All loan outs of this product. You can see if it's still out or if it has been returned. You can also see if you have sent any reminders.</Text>,
    titleChildrenProductsText: <Text id='product.children-list-title'>Connected products</Text>,
    titleParentProductsText: <Text id='product.product-list-title'>Parent product</Text>,
}))
@observer
class Product extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
        };
        this.mainContainer = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id, uuidv4, race, customer, skipUpdate, showPickProduct = false, callback = () => {} } = props;
        const { productStore, appState, userStore } = this.props.stores;
        const { user = {} } = userStore;

        if (emptyFirst && !skipUpdate) {
            productStore.updateKeyValue('product', {});
        }
        // await productStore.load(id, false, { query:{ installationId: installation.id }, addData: ['installations'] });
        const query = {
            customer,
        };
        if (id) {
            query.id = id;
        } else if (uuidv4) {
            query.uuidv4 = uuidv4;
        }
        // Stock picking (/products/pick/:id via the QR scanner) is an authenticated,
        // tenant-scoped action on internal stock items that are usually NOT webshop
        // products. The public endpoint forces webshop:1 on a numeric-id lookup, so
        // those came back empty. Logged-in pickers load through the authenticated
        // product endpoint (the store's default /api/products/) instead; the webshop
        // and share views keep the public endpoint.
        const usePublicEndpoint = !(showPickProduct && user.id);
        await productStore.load(id, false, {
            query,
            addData: ['likes', 'comments', 'tags'],
            skipUpdate,
            url: usePublicEndpoint ? `/api/products/public/` : undefined,
        });

        if (uuidv4) {
            // If we loaded by uuidv4, we need to set the product from the products array.
            const { products = [] } = productStore;
            const product = products.find(p => p.uuidv4 === uuidv4);
            if (product) {
                productStore.updateKeyValue('product', product);
            } else {
                console.warn(`No product found with uuidv4: ${uuidv4}`);
            }
        }

        // Stats: log a product_view usage event and open a view session for this product.
        // The session is posted as a scrollView (type productView) when the drawer closes.
        this.postViewSession();
        const { product: viewedProduct = {} } = productStore;
        if (viewedProduct.id) {
            this.viewedProductId = viewedProduct.id;
            this.viewStartTime = new Date().getTime();
            appState.trackProduct('view', viewedProduct);
        }

        callback();
    }

    postViewSession = () => {
        const { productStore } = this.props.stores;
        const { sessionid } = this.state;
        const productId = this.viewedProductId;
        const startTime = this.viewStartTime;
        if (!productId || !startTime) {
            return;
        }
        this.viewStartTime = null;
        const endTime = new Date().getTime();
        productStore.postScrollview([{
            type: 'productView',
            elementId: productId,
            intersectionRatio: 1,
            startTime,
            endTime,
            duration: endTime - startTime,
            sessionId: `${sessionid}`,
        }]);
    }

    openStatsDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, productStore } = this.props.stores;
        const { product = {} } = productStore;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('productStats', {
            height: drawerHeightLarge,
            productId: product.id,
        }, drawerLevel + 1);
    }

    editProduct = () => {
		const { id, drawerLevel = 1, skipUpdate } = this.props;
		const { appState } = this.props.stores;

		const { drawerHeightMedium } = appState;
		appState.openDrawer('editProduct', {
			height: drawerHeightMedium,
			id,
            skipUpdate,
		}, drawerLevel + 1);
	}

    selectColor = (name) => {
        this.setState({ color: toggleVariant(this.state.color, name) });
    }

    selectSize = (name) => {
        this.setState({ size: toggleVariant(this.state.size, name) });
    }

    addToCart = async () => {
        const { color, size } = this.state;
        const { race } = this.props;
        const { productStore, userStore, appState } = this.props.stores;
        const { product } = productStore;
        const { user } = userStore;
        if (!user.id) {
            return;
        }
        await userStore.addToCart({
            color,
            size,
            product: product.id,
            qty: 1,
            race,
        });
        appState.trackProduct('add_to_cart', product, { color, size, race });
        // await userStore.getInfo({ forceLoad: true });
    }

    gotoCart = () => {
        const { appState } = this.props.stores;
        appState.toggleDrawer();
        route('/cart');
    }

    // Guest (not logged in) basket: available when the shop that owns this
    // product takes Vipps and the guestCartStore has been loaded for it (the
    // race page does that for anonymous visitors).
    showGuestBuy = () => {
        const { guestCartStore } = this.props.stores;
        const { productStore } = this.props.stores;
        const { product = {} } = productStore;
        return !!(guestCartStore && guestCartStore.canCheckout && product.id
            && guestCartStore.customer === product.customer);
    }

    addToGuestCart = () => {
        const { color, size } = this.state;
        const { race } = this.props;
        const { productStore, guestCartStore, appState } = this.props.stores;
        const { product } = productStore;
        const ok = guestCartStore.add({
            product, qty: 1, color, size, race,
        });
        this.setState({ guestAdded: ok, guestOtherShop: !ok });
        if (ok) {
            appState.trackProduct('add_to_cart', product, {
                color, size, race, guest: true,
            });
        }
    }

    openGuestCart = () => {
        const { appState } = this.props.stores;
        const { race, drawerLevel = 1 } = this.props;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('guestCart', {
            race,
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    qtyChanged = e => {
        const value = e.target.value;
        if (value === '' || (/^\d+$/.test(value) && parseInt(value, 10) > 0)) {
            this.setState({ pickQty: value });
        }
    }

    pickProductsFromStock = async () => {
        const { pickQty = 1 } = this.state;
        const { productStore, userStore } = this.props.stores;
        const { product } = productStore;
        if (!product.id || !pickQty || parseInt(pickQty, 10) <= 0) {
            return;
        }
        await productStore.pickFromStock(product.id, parseInt(pickQty, 10));
        this.setState({ pickQty: 1, productPicked: true });

        this.pickMessageTimer = setTimeout(() => {
            this.setState({ productPicked: false });
        }, 3000);

    }

    continueShopping = () => {
        const { appState } = this.props.stores;
        appState.toggleDrawer();
    }

    componentDidMount() {
        this.loadAll();
        decorateExternalLinks(this.mainContainer);
    }

    componentDidUpdate() {
        decorateExternalLinks(this.mainContainer);
    }


    viewTags = obj => {
        if (obj.tags?.length === 0 && obj.loans?.length === 0 && !obj.ownedByOther && !obj.url && !obj.parents?.length > 0 && !obj.files?.length > 0) {
            return '';
        }
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const tagClass = darkmode ? 'dark' : 'light';

        return (<>
            <div class='w-100 overflow-hidden'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {obj.files?.length > 0 && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>}
                    {obj.tags?.length > 0 && obj.tags.map(tag => {
                        return (<>
                            <small
                                class={`me-1 mt-1`}
                                onClick={this.toggleTagFilter}
                                data-tag={tag.name}
                            >#{tag.name}</small>
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid) {
            this.loadAll(nextProps);
        }
    }

    componentWillUnmount() {
        this.postViewSession();
    }

    render() {
        const {
            color, size, pickQty = 1, productPicked, guestAdded = false, guestOtherShop = false,
        } = this.state;
        const {
            drawerLevel = 1,
            showBreadcrumbs = false,
            showEdit = true,
            showPickProduct = false,
            isSubscriptionView = false,
            showShareProduct = true,
        } = this.props;
        const { userStore, productStore, guestCartStore = {} } = this.props.stores;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { cart = [] } = user;
        const { product = {}, products = [] } = productStore;

        const hasProductInBasket = cart.find(item => item.product === product.id);
        const productHasColors = product.colors && product.colors.length > 0;
        const productHasSizes = product.sizes && product.sizes.length > 0;

        const colorIdxChosen = productHasColors ? product.colors.findIndex(c => c.name === color) + 1 : 0;

        return (<>
            <div class='w-100 d-flex justify-content-center' ref={c => { this.mainContainer = c; }}>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    {(isCustomerAdmin || isAdmin) && showEdit && <div class='position-absolute d-flex flex-row gap-2' style='top: 15px; left: 15px; z-index: 1000;'>
                        {isCustomerAdmin && <button onClick={this.editProduct} class={`btn btn-outline-primary rounded-pill`}>
                            <i class={`fa-regular fa-pen ms-3 me-1`} /> <Text id='product.edit'>Edit</Text>
                        </button>}
                        <button onClick={this.openStatsDrawer} class='btn btn-outline-secondary rounded-pill' title='Show product statistics'>
                            <i class='fa-solid fa-chart-simple ms-3 me-1' /> <Text id='product.stats'>Stats</Text>
                        </button>
                    </div>}

                    <div class='w-100 d-flex flex-column'>
                        <div class='px-0 box-header-info d-flex flex-column'>
                            {showBreadcrumbs &&<div class={`d-flex flex-row mt-3 mb-2 mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3`}>
                                <small class='text-muted'>
                                    <a href='/'>
                                        <i class='fa-duotone fa-home' />
                                    </a>
                                </small>
                                <small class='mx-2 text-muted'>/</small>
                                <small class='text-muted'>
                                    <a href='/products'>
                                        <Text id='product.products'>Products</Text>
                                    </a>
                                </small>
                            </div>}

                            <div class='d-flex px-3 position-relative'>
                                <ImageScroller images={product.images} stores={this.props.stores} showImg={img => localUtil.displayImg(img, this.props, '1024x')} maxHeight={'50vh'} imageIdx={colorIdxChosen} />
                                {product.saleType && <>
                                    <div class='position-absolute start-0 bottom-0 w-100 px-4 py-2' style='background: rgba(220, 53, 69, 0.9);'>
                                        <span class='text-white fw-bold' style='font-size: 1.1em;'>{product.saleType}</span>
                                    </div>
                                </>}
                            </div>
                            <Stl3dSection models={product.models} entityPlural='products' id={product.id} />
                            <div class={`d-flex flex-row mt-3 mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                {/* <div
                                    class='box-line-qty text-end'
                                    onClick={this.editProductQty}
                                    data-id={product.id}
                                    data-qty={product.qty}
                                >
                                    {product.qty}
                                </div> */}
                                <div class={`flex-fill d-flex flex-column px-3 py-3 text-overflow position-relative`}>
                                    <GradientRight stores={this.props.stores} styles={'border-radius: 0 1.0rem 1.0rem 0 !important;'} />

                                    {product.tags && product.tags.length > 0 && <div class='d-flex flex-row mb-1'>
                                        {this.viewTags(product)}
                                    </div>}

                                    {product.name && <div class='d-flex flex-row'>
                                        {!isSubscriptionView && <span class='font-weight-lighter text-muted'>
                                            <Text id='product.name'>Name</Text>
                                        </span>}
                                        <span class={isSubscriptionView ? '' : 'ms-2'}>{product.name}</span>
                                    </div>}

                                    {['productNumber', 'currency', 'inStock', 'price'].map(key => {
                                        if (!product[key]) {
                                            return '';
                                        }
                                        const field = fields.getField('product', key);
                                        const displayValue = field.displayPublicValue || (value => value);

                                        // Special handling for price with discount
                                        // totalprice = price + vat - discount (discount is including vat)
                                        if (key === 'price' && product.discountAmount > 0) {
                                            const priceIncVat = product.price * (1 + (product.vat || 0) / 100);
                                            return (<>
                                                <div class='d-flex flex-row justify-content-end pe-2 align-items-baseline'>
                                                    <span class='text-muted me-2' style='font-size: 1.0rem; text-decoration: line-through;'>
                                                        kr. {util.format(priceIncVat, 2, ',', ' ')}
                                                    </span>
                                                    <span class='text-danger fw-bold' style='font-size: 1.8rem;'>
                                                        kr. {util.format(priceIncVat - product.discountAmount, 2, ',', ' ')}
                                                    </span>
                                                </div>
                                            </>);
                                        }

                                        return (<>
                                            <div class={`d-flex flex-row ${key === 'price' ? 'justify-content-end pe-2' : ''}`} style={`${key === 'price' ? 'font-size: 1.5rem;' : ''}`}>
                                                {key === 'price' ? '' : <span class='font-weight-lighter text-muted'>
                                                    {field.title}
                                                </span>}
                                                {field.type === 'textarea' ? <>
                                                    <div class='ms-2  mb-4 text-wrap' style='max-height: 50vh; overflow: auto;'>
                                                        <Markdown markdown={displayValue(product[key])} markedOpts={MARKDOWN_OPTIONS} />
                                                    </div>
                                                </> : <>
                                                    <span class='ms-2'>
                                                        {displayValue(product[key], product)}
                                                    </span>
                                                </>}
                                            </div>
                                        </>);
                                    })}

                                    {hasProductInBasket ? <>
                                        <i class='fa-duotone fa-check-circle text-success' style='font-size: 2.0em;' />
                                    </> : <></>}

                                    {showPickProduct ? <></> : <>
                                        {['description', 'technicalInfo', 'properties'].map((key, idx) => {
                                            if (!product[key]) {
                                                return '';
                                            }
                                            const field = fields.getField('product', key);
                                            const displayValue = field.displayPublicValue || (value => value);
                                            return (<>
                                                <div class={`d-flex flex-column mt-3`}>
                                                    {!isSubscriptionView && <span class='font-weight-lighter box-header text-muted'>
                                                        {field.title}
                                                    </span>}
                                                    {field.type === 'textarea' ? <>
                                                        <div class={`${isSubscriptionView ? '' : 'ms-2'} mb-4 text-wrap`} style='max-height: 50vh; overflow: auto;'>
                                                            <Markdown markdown={displayValue(product[key])} markedOpts={MARKDOWN_OPTIONS} />
                                                        </div>
                                                    </> : <>
                                                        <span class={isSubscriptionView ? '' : 'ms-2'}>
                                                            {displayValue(product[key], product)}
                                                        </span>
                                                    </>}
                                                </div>
                                            </>);
                                        })}
                                    </>}

                                </div>
                            </div>

                            {showPickProduct && <>
                                {user.id ? <>
                                    <div class={`d-flex flex-column mt-3 mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                        <div class={`d-flex flex-row px-3 py-3 position-relative justify-content-center align-items-center w-100`}>

                                            <div class='input-group d-flex flex-row justify-content-center'>
                                                <div class='input-group-text rounded-pill-left'>
                                                    <i class='fa-solid fa-box-open'></i>
                                                </div>
                                                <Localizer>
                                                    <Input
                                                        stores={this.props.stores}
                                                        holdingStore={productStore}
                                                        object={{
                                                            pickQty,
                                                        }}
                                                        type={'number'}
                                                        field={'pickQty'}
                                                        value={pickQty}
                                                        placeholder={'qty'}
                                                        onInput={this.qtyChanged}
                                                        extraDataset={{
                                                            'data-id': product.id,
                                                        }}
                                                        isTable={true}
                                                        setFocusToThis={true}
                                                        styles='max-width: 100px;'
                                                    />
                                                </ Localizer>
                                                {pickQty && <>
                                                    <button class='btn btn-primary btn-sm rounded-pill-right' type='button' onClick={this.pickProductsFromStock}>
                                                        <i class='fa-solid fa-bag-shopping' /> <Text id='product.pick'>Pick</Text>
                                                    </button>
                                                </>}
                                            </div>
                                        </div>

                                        {productPicked && <>
                                            <div class={`d-flex flex-row px-3 py-3 position-relative justify-content-center align-items-center w-100`}>
                                                <span class={`badge text-bg-success font-weight-normal me-1 mt-1`}>
                                                    <i class='fa-solid fa-check me-1' /><Text id='product.products-picked'>Products picked from stock</Text>
                                                </span>
                                            </div>
                                        </>}
                                    </div>
                                    <div class={`d-flex flex-row px-3 py-3 position-relative justify-content-center align-items-center w-100 mt-3`}>
                                        <button class='btn btn-lg btn-block btn-primary rounded-pill w-100' onClick={() => route('/products/qrcodeScanner')}>
                                            <i class='fa-solid fa-qrcode' /> <Text id='product.scan-another'>Scan another</Text>
                                        </button>
                                    </div>
                                </> : <>
                                    <div class={`flex-fill d-flex flex-row justify-content-center px-3 mt-3`}>
                                        <Text id='user.login-to-pick-products'>You must login to be able to pick products.</Text>
                                    </div>
                                    <div class={`flex-fill d-flex flex-row justify-content-center px-3 mt-3`} >
                                        <button class='btn btn-lg btn-block btn-primary rounded-pill' onClick={() => route('/login')}>
                                            <i class='fa-duotone fa-right-to-bracket me-2' /> <Text id='user.goto-login'>Goto Login</Text>
                                        </button>
                                    </div>
                                </>}
                            </>}


                            {!showPickProduct && <>
                                {user.id ? <>
                                    <div class={`flex-fill d-flex flex-column px-3 py-3 mt-3 justify-content-center align-items-center fixed-bottom ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`} style={`bottom: 0px; left: 0;`}>
                                        {product.inStock > 0 && productHasColors && <VariantPills
                                            title={<Text id='product.select-color'>Select color</Text>}
                                            options={product.colors}
                                            selected={color}
                                            onSelect={this.selectColor}
                                        />}
                                        {product.inStock > 0 && productHasSizes && <VariantPills
                                            title={<Text id='product.select-size'>Select size</Text>}
                                            options={product.sizes}
                                            selected={size}
                                            onSelect={this.selectSize}
                                        />}

                                        <div class='d-flex flex-row justify-content-center align-items-center'>
                                            {hasProductInBasket ? <>
                                                <button
                                                    class={`btn btn-success btn-lg rounded-pill mx-1`}
                                                    onClick={this.gotoCart}
                                                >
                                                    <i class='fa-duotone fa-cart-shopping' /> <Text id='product.goto-cart'>Goto cart</Text>
                                                </button>
                                                {/* <button
                                                    class={`btn btn-secondary btn-lg rounded-pill mx-1`}
                                                    onClick={this.continueShopping}
                                                >
                                                    <i class='fa-duotone fa-cart-plus' /> <Text id='product.continue-shopping'>Continue shopping</Text>
                                                </button> */}
                                            </> : <></>}

                                            {product.inStock > 0 ? <>
                                                <button
                                                    disabled={(!color && productHasColors) || (!size && productHasSizes)}
                                                    class={`btn btn-primary btn-lg rounded-pill mx-1`}
                                                    onClick={this.addToCart}
                                                >
                                                    <i class='fa-duotone fa-cart-plus' /> <Text id='product.add-to-cart'>Add to cart</Text>
                                                </button>
                                            </> : <>
                                                <button
                                                    class={`btn btn-block btn-danger btn-lg rounded-pill mx-1`}
                                                    disabled
                                                >
                                                    <i class='fa-duotone fa-cart-plus' /> <Text id='product.out-of-stock'>Out of stock</Text>
                                                </button>
                                            </>}
                                        </div>
                                    </div>
                                </> : <>
                                    {this.showGuestBuy() ? <GuestBuyBox
                                        product={product}
                                        color={color}
                                        size={size}
                                        count={guestCartStore.count}
                                        added={guestAdded}
                                        otherShop={guestOtherShop}
                                        onAdd={this.addToGuestCart}
                                        onOpenCart={this.openGuestCart}
                                        onSelectColor={this.selectColor}
                                        onSelectSize={this.selectSize}
                                    /> : <>
                                        <div class={`flex-fill d-flex flex-row justify-content-center px-3 mt-3`}>
                                            <Text id='user.login-to-add-to-cart'>You must login to be able to add products to your cart.</Text>
                                        </div>
                                        <div class={`flex-fill d-flex flex-row justify-content-center px-3 mt-3`} >
                                            <button class='btn btn-lg btn-block btn-primary rounded-pill' onClick={() => route('/login')}>
                                                <i class='fa-duotone fa-right-to-bracket me-2' /> <Text id='user.goto-login'>Goto Login</Text>
                                            </button>
                                        </div>
                                    </>}
                                </>}
                            </>}

                            {!isSubscriptionView && <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <Likes
                                    stores={this.props.stores}
                                    type={'product'}
                                    objectId={product.id}
                                    likes={product.likes}
                                    showShare={true}
                                    uuidv4={product.uuidv4}
                                    shareTitle={product.name}
                                    shareSummary={product.description}
                                    shareImage={product.images?.[0]?.s3SmallLink}
                                />

                                <Comments stores={this.props.stores} type={'product'} objectId={product.id} comments={product.comments} />
                            </div>}

                            {/* Same copy-link row as the article view: the share URL carries the
                                OG preview and redirects to the product page, so it is the one to
                                use in ads and posts elsewhere. */}
                            {!isSubscriptionView && showShareProduct && product.uuidv4 && <div class='d-flex flex-row justify-content-center mx-3 mt-4 rounded-lg px-4 py-3'>
                                <small>
                                    <ClipboardCopy
                                        label={<><i class='fa-duotone fa-share' /> <Text id='product.share'>Share product</Text></>}
                                        text={shareUrlFor('product', product.uuidv4)}
                                    />
                                </small>
                            </div>}

                            {/* hasProductInBasket: {hasProductInBasket ? 'true' : 'false'} */}


                                {/*
                                    TODO: New features:
                                    - Show history for product
                                    - Show loan status for product
                                    - Track usage of product: Hours, distance, position, etc.
                                */}



                        </div>
                    </div>


                </div>
            </div>
        </>);
    }
}

export default Product;
